import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronDown } from "lucide-react";
import { customersAPI } from "../../services";

const MAX_RANGE_DAYS = 30;

const HEALTH_LABELS = {
  active: "Active",
  over_limit: "Over limit",
  credit_outstanding: "Credit outstanding",
  dormant: "Dormant",
  no_purchases: "No purchases",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const berlinTodayIso = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (type) => parts.find((part) => part.type === type)?.value;
  return `${value("year")}-${value("month")}-${value("day")}`;
};

const addDaysIso = (isoDate, deltaDays) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + deltaDays);
  const nextYear = date.getUTCFullYear();
  const nextMonth = String(date.getUTCMonth() + 1).padStart(2, "0");
  const nextDay = String(date.getUTCDate()).padStart(2, "0");
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

const rangeForDays = (days) => {
  const endDate = berlinTodayIso();
  const startDate = addDaysIso(endDate, -(days - 1));
  return { startDate, endDate };
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `€${amount.toFixed(2)}`;
};

const moneyClassName = (value) =>
  Number(value) < 0 ? "customer-analytics-card-value negative" : "customer-analytics-card-value";

const formatDate = (value) => {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return String(value);
  return `${match[3]} ${month} ${match[1]}`;
};

const isoToDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const dateToIso = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const inclusiveDaySpan = (startIso, endIso) => {
  const [startYear, startMonth, startDay] = startIso.split("-").map(Number);
  const [endYear, endMonth, endDay] = endIso.split("-").map(Number);
  const start = Date.UTC(startYear, startMonth - 1, startDay);
  const end = Date.UTC(endYear, endMonth - 1, endDay);
  return Math.floor(Math.abs(end - start) / 86400000) + 1;
};

const formatRangeLabel = (startIso, endIso) => {
  const startLabel = isoToDate(startIso).toLocaleDateString("en-GB");
  if (!endIso) return `${startLabel} - `;
  return `${startLabel} - ${isoToDate(endIso).toLocaleDateString("en-GB")}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return `${amount.toFixed(1)}%`;
};

const formatDays = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  const label = Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
  return `${label} days`;
};

const CustomerAnalytics = ({
  customerId,
  accentColor,
  themeClass,
  dateSlot,
}) => {
  const initialRange = rangeForDays(MAX_RANGE_DAYS);
  const [rangeStart, setRangeStart] = useState(initialRange.startDate);
  const [rangeEnd, setRangeEnd] = useState(initialRange.endDate);
  const [draftStart, setDraftStart] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() =>
    isoToDate(initialRange.endDate)
  );
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!customerId || !rangeStart || !rangeEnd) return;

    let cancelled = false;

    const loadAnalytics = async () => {
      setLoading(true);
      setError("");
      const result = await customersAPI.getCustomerAnalytics(customerId, {
        startDate: rangeStart,
        endDate: rangeEnd,
      });
      if (cancelled) return;

      const payload = result.response?.data;
      if (result.error || !payload || payload.customer_id == null) {
        setAnalytics(null);
        setError(result.error?.message || "Failed to fetch customer analytics");
      } else {
        setAnalytics(payload);
      }
      setLoading(false);
    };

    loadAnalytics();
    return () => {
      cancelled = true;
    };
  }, [customerId, rangeStart, rangeEnd]);

  const todayIso = berlinTodayIso();
  const displayStart = draftStart || rangeStart;
  const displayEnd = draftStart ? null : rangeEnd;

  const isDateDisabled = (date) => {
    const iso = dateToIso(date);
    if (iso > todayIso) return true;
    if (draftStart && inclusiveDaySpan(draftStart, iso) > MAX_RANGE_DAYS) {
      return true;
    }
    return false;
  };

  const isDateInRange = (date) => {
    if (!displayStart || !displayEnd) return false;
    const iso = dateToIso(date);
    return iso >= displayStart && iso <= displayEnd;
  };

  const isDateSelected = (date) => {
    const iso = dateToIso(date);
    return iso === displayStart || iso === displayEnd;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    const iso = dateToIso(date);

    if (!draftStart) {
      setDraftStart(iso);
      return;
    }

    let start = draftStart;
    let end = iso;
    if (end < start) {
      start = iso;
      end = draftStart;
    }
    if (inclusiveDaySpan(start, end) > MAX_RANGE_DAYS) return;

    setRangeStart(start);
    setRangeEnd(end);
    setDraftStart(null);
    setShowDatePicker(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(
      (month) => new Date(month.getFullYear(), month.getMonth() + direction, 1)
    );
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i += 1) {
      days.push(null);
    }
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const healthKey = analytics?.health || "";
  const limitUsed = Number(analytics?.limit_used_pct);
  const limitBarWidth = Number.isFinite(limitUsed)
    ? Math.min(Math.max(limitUsed, 0), 100)
    : 0;

  const dateFilter = (
        <div className="customer-details-date-range customer-analytics-date-range">
          <Calendar size={16} className="customer-details-date-icon" />
          <input
            type="text"
            className={themeClass("customer-details-date-input")}
            value={formatRangeLabel(displayStart, displayEnd)}
            readOnly
            onClick={() => setShowDatePicker((open) => !open)}
          />
          {showDatePicker && (
            <>
              <div
                className="date-picker-backdrop"
                onClick={() => {
                  setDraftStart(null);
                  setShowDatePicker(false);
                }}
              />
              <div className="customer-detail-date-picker">
                <div className="date-picker-header">
                  <button
                    type="button"
                    className="date-picker-nav-btn"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </button>
                  <h3 className="date-picker-month-year">
                    {currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h3>
                  <button
                    type="button"
                    className="date-picker-nav-btn"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(-90deg)" }}
                    />
                  </button>
                </div>
                <div className="date-picker-weekdays">
                  <div className="weekday">Mo</div>
                  <div className="weekday">Tu</div>
                  <div className="weekday">We</div>
                  <div className="weekday">Th</div>
                  <div className="weekday">Fr</div>
                  <div className="weekday">Sa</div>
                  <div className="weekday">Su</div>
                </div>
                <div className="date-picker-days">
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div
                      key={index}
                      className={`${themeClass("date-picker-day")} ${
                        day ? "" : "empty"
                      } ${day && isDateDisabled(day) ? "disabled" : ""} ${
                        day && isDateInRange(day) ? "in-range" : ""
                      } ${day && isDateSelected(day) ? "selected" : ""}`}
                      onClick={() => day && handleDateClick(day)}
                    >
                      {day ? day.getDate() : ""}
                    </div>
                  ))}
                </div>
                <p className="customer-analytics-range-hint">
                  Maximum range is 30 days
                </p>
              </div>
            </>
          )}
        </div>
  );

  return (
    <div
      className="customer-analytics"
      style={accentColor ? { "--analytics-accent": accentColor } : undefined}
    >
      {dateSlot ? createPortal(dateFilter, dateSlot) : dateFilter}

      {loading ? (
        <div className="customer-analytics-state">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="customer-analytics-state customer-analytics-error">
          {error}
        </div>
      ) : analytics ? (
        <>
          <section>
            <div className="customer-analytics-section-head">
              <h3 className="customer-analytics-section-title">All time</h3>
              <span className={`customer-analytics-health ${healthKey}`}>
                {HEALTH_LABELS[healthKey] || healthKey || "—"}
              </span>
            </div>
            <div className="customer-analytics-grid">
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Orders</p>
                <p className="customer-analytics-card-value">
                  {analytics.orders_total ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Revenue</p>
                <p className="customer-analytics-card-value">
                  {formatMoney(analytics.revenue_total)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Profit</p>
                <p className={moneyClassName(analytics.profit_total)}>
                  {formatMoney(analytics.profit_total)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Returns</p>
                <p className="customer-analytics-card-value">
                  {analytics.returns_total ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">First purchase</p>
                <p className="customer-analytics-card-value date">
                  {formatDate(analytics.first_purchase)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Last purchase</p>
                <p className="customer-analytics-card-value date">
                  {formatDate(analytics.last_purchase)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Last payment</p>
                <p className="customer-analytics-card-value date">
                  {formatDate(analytics.last_payment)}
                </p>
              </article>
            </div>
          </section>

          <section>
            <h3 className="customer-analytics-section-title">Credit</h3>
            <div className="customer-analytics-grid">
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Balance</p>
                <p className={moneyClassName(analytics.balance)}>
                  {formatMoney(analytics.balance)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Credit limit</p>
                <p className="customer-analytics-card-value">
                  {analytics.credit_limit === null
                    ? "No limit"
                    : formatMoney(analytics.credit_limit)}
                </p>
              </article>
              <article
                className={`customer-analytics-card${
                  analytics.over_limit ? " warning" : ""
                }`}
              >
                <p className="customer-analytics-card-label">Outstanding</p>
                <p className="customer-analytics-card-value">
                  {formatMoney(analytics.outstanding)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Limit used</p>
                <p className="customer-analytics-card-value">
                  {formatPercent(analytics.limit_used_pct)}
                </p>
                {analytics.limit_used_pct !== null &&
                  analytics.limit_used_pct !== undefined && (
                    <div className="customer-analytics-bar">
                      <span
                        className={`customer-analytics-bar-fill${
                          analytics.over_limit ? " warning" : ""
                        }`}
                        style={{ width: `${limitBarWidth}%` }}
                      />
                    </div>
                  )}
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Outstanding since</p>
                <p className="customer-analytics-card-value date">
                  {formatDate(analytics.credit?.outstanding_since)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Outstanding days</p>
                <p className="customer-analytics-card-value">
                  {formatDays(analytics.credit?.outstanding_days)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Last credit paid</p>
                <p className="customer-analytics-card-value date">
                  {formatDate(analytics.credit?.last_credit_paid_at)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Last credit amount</p>
                <p className="customer-analytics-card-value">
                  {formatMoney(analytics.credit?.last_credit_paid_amount)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Days since last payment</p>
                <p className="customer-analytics-card-value">
                  {formatDays(analytics.credit?.days_since_last_credit_payment)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Credit payments</p>
                <p className="customer-analytics-card-value">
                  {analytics.credit?.credit_payments_total ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Payments in period</p>
                <p className="customer-analytics-card-value">
                  {analytics.credit?.credit_payments_in_period ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Avg days between payments</p>
                <p className="customer-analytics-card-value">
                  {formatDays(analytics.credit?.avg_days_between_credit_payments)}
                </p>
              </article>
            </div>
          </section>

          <section>
            <h3 className="customer-analytics-section-title">This period</h3>
            <div className="customer-analytics-grid">
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Orders</p>
                <p className="customer-analytics-card-value">
                  {analytics.period_orders ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Revenue</p>
                <p className="customer-analytics-card-value">
                  {formatMoney(analytics.period_revenue)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Profit</p>
                <p className={moneyClassName(analytics.period_profit)}>
                  {formatMoney(analytics.period_profit)}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Returns</p>
                <p className="customer-analytics-card-value">
                  {analytics.period_returns ?? 0}
                </p>
              </article>
              <article className="customer-analytics-card">
                <p className="customer-analytics-card-label">Credit sales</p>
                <p className="customer-analytics-card-value">
                  {analytics.period_credit_sales ?? 0}
                </p>
              </article>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default CustomerAnalytics;
